import axios from "axios";

// ---- Config (all optional; safe defaults) ----
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
// Static mode = no FastAPI backend (e.g. Cloudflare Pages). Auto-on when no backend URL.
const STATIC_MODE = process.env.REACT_APP_STATIC_MODE === "true" || !BACKEND_URL;

// Where static form submissions are POSTed. Defaults to the built-in Cloudflare
// Pages Function ("/api/submit"). Can be overridden with a Formspree/Web3Forms URL.
// If the endpoint is unreachable (e.g. pure static host with no function), it
// automatically falls back to opening the visitor's email app.
const FORM_ENDPOINT = process.env.REACT_APP_FORM_ENDPOINT || "/api/submit";
const OWNER_EMAIL = process.env.REACT_APP_OWNER_EMAIL || "healthyhomebakerysf@gmail.com";
const PAYPAL_ME = process.env.REACT_APP_PAYPAL_ME || "https://www.paypal.biz/healthyhomebakerysf";
const ZELLE_EMAIL = process.env.REACT_APP_ZELLE_EMAIL || OWNER_EMAIL;
const STRIPE_LINK = process.env.REACT_APP_STRIPE_PAYMENT_LINK || "";

export const API = BACKEND_URL ? `${BACKEND_URL}/api` : "";
const http = axios.create({ baseURL: API });

// ---- Admin (httpOnly cookie session — token never stored in JS/localStorage) ----
// With a FastAPI backend: <BACKEND_URL>/api. On Cloudflare (no backend): the Worker's
// own /api routes (server-lib/admin.js) on the same site.
const ADMIN_API = BACKEND_URL ? `${BACKEND_URL}/api` : "/api";

async function adminGet(path) {
  const r = await fetch(`${ADMIN_API}${path}`, { credentials: "include" });
  if (r.status === 403) throw new Error("unauthorized");
  if (!r.ok) throw new Error("request_failed");
  return r.json();
}
export const getCustomOrders = () => adminGet("/custom-orders");

// ---- Product catalog (Cloudflare Worker + D1, see server-lib/products.js) ----
// REACT_APP_PRODUCTS_API lets the site read products from a Worker on another
// domain (add this site's origin to the Worker's ALLOWED_ORIGINS). Defaults to
// this site's own /api.
const PRODUCTS_API = (process.env.REACT_APP_PRODUCTS_API || "/api").replace(/\/+$/, "");

async function readJson(r) {
  const d = await r.json().catch(() => ({}));
  if (r.status === 403) throw new Error("unauthorized");
  if (!r.ok) throw new Error(typeof d.detail === "string" ? d.detail : "Request failed");
  return d;
}

// Public: always fresh (the Worker sends no-store, and so do we).
export async function getProducts() {
  const r = await fetch(`${PRODUCTS_API}/products`, { cache: "no-store", credentials: "include" });
  const d = await readJson(r);
  if (!Array.isArray(d)) throw new Error("Bad product list");
  return d;
}

export async function getSettings() {
  const r = await fetch(`${PRODUCTS_API}/settings`, { cache: "no-store", credentials: "include" });
  return readJson(r);
}

// Admin: partial update — any of { name, description, image_url, sizes, quantity }.
// Resolves with the saved product exactly as stored in D1.
export async function updateProduct(id, patch) {
  const r = await fetch(`${PRODUCTS_API}/products/${encodeURIComponent(id)}`, {
    method: "PUT",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(patch),
  });
  return readJson(r);
}

// Admin: upload a photo (Blob/File). Resolves with the updated product.
export async function uploadProductImage(id, blob) {
  const r = await fetch(`${PRODUCTS_API}/products/${encodeURIComponent(id)}/image`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": blob.type || "image/jpeg" },
    body: blob,
  });
  return readJson(r);
}

export async function updateSettings(patch) {
  const r = await fetch(`${PRODUCTS_API}/settings`, {
    method: "PUT",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(patch),
  });
  return readJson(r);
}
export const getInquiries = () => adminGet("/inquiries");

// Admin: update a paid order's status (sends the customer a branded email).
export async function updateOrderStatus(sessionId, status) {
  const r = await fetch(`${ADMIN_API}/orders/${sessionId}/status`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ status }),
  });
  if (r.status === 403) throw new Error("unauthorized");
  const d = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(typeof d.detail === "string" ? d.detail : "Update failed");
  return d;
}

// Verifies admin email + password; server sets an httpOnly session cookie.
export async function adminLogin(email, password) {
  const r = await fetch(`${ADMIN_API}/admin/login`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email, password }),
  });
  const d = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(typeof d.detail === "string" ? d.detail : "Invalid email or password");
  return d;
}

export async function adminLogout() {
  try {
    await fetch(`${ADMIN_API}/admin/logout`, { method: "POST", credentials: "include" });
  } catch (e) {
    /* ignore network error on logout */
  }
}

// Returns true if a valid admin session cookie already exists.
export async function adminMe() {
  try {
    const r = await fetch(`${ADMIN_API}/admin/me`, { credentials: "include" });
    return r.ok;
  } catch (e) {
    return false;
  }
}

export async function adminForgotPassword(email) {
  await fetch(`${ADMIN_API}/admin/forgot-password`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email }),
  });
  return true;
}

export async function adminResetPassword(token, password) {
  const r = await fetch(`${ADMIN_API}/admin/reset-password`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ token, password }),
  });
  const d = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(typeof d.detail === "string" ? d.detail : "Reset failed");
  return d;
}

// ---- Stripe Checkout (real card payments) ----
// Works both with the FastAPI backend (Emergent) and on a static Cloudflare Pages
// deploy (Cloudflare Pages Functions at /api/payments/*). Always uses the /api prefix.
// Never sends an amount — the server uses a fixed server-side price.
const PAY_BASE = BACKEND_URL ? `${BACKEND_URL}/api` : "/api";

export async function startStripeCheckout({ order_id, name, email } = {}) {
  const r = await fetch(`${PAY_BASE}/payments/checkout`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      origin_url: window.location.origin,
      order_id: order_id || "",
      name: name || "",
      email: email || "",
    }),
  });
  const d = await r.json().catch(() => ({}));
  const url = d.checkout_url;
  if (!url) throw new Error(d.error || "No checkout URL returned");
  window.location.href = url;
  return d;
}

export async function getPaymentStatus(sessionId) {
  const r = await fetch(`${PAY_BASE}/payments/status/${sessionId}`);
  if (!r.ok) throw new Error("status_failed");
  return r.json();
}

// Basket: full-total card checkout. Items = [{id, size, quantity}].
export async function startCartCheckout({ items, name, email, fulfillment, address }) {
  const r = await fetch(`${PAY_BASE}/payments/cart-checkout`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ origin_url: window.location.origin, items, name, email, fulfillment, address }),
  });
  const d = await r.json().catch(() => ({}));
  if (!d.checkout_url) throw new Error(d.detail || "Checkout failed");
  window.location.href = d.checkout_url;
  return d;
}

// Basket: PayPal/Zelle manual total instructions.
export async function cartManual({ items, method, name, email, fulfillment, address }) {
  const r = await fetch(`${PAY_BASE}/payments/cart-manual`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ items, method, name, email, fulfillment, address }),
  });
  const d = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(d.detail || "Failed");
  return d;
}

// Basket: real PayPal Checkout (Smart Buttons). Creates a server-priced PayPal order.
export async function createCartPaypalOrder({ items, name, email, fulfillment, address }) {
  const r = await fetch(`${PAY_BASE}/paypal/cart-create-order`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ items, name, email, fulfillment, address }),
  });
  const d = await r.json().catch(() => ({}));
  if (!d.id) throw new Error(d.detail || "Could not start PayPal checkout");
  return d;
}

// Basket: capture an approved PayPal order server-side.
// `details` (name/email/items) is only used for the emailed receipt — the amount is
// always verified server-side.
export async function captureCartPaypalOrder(orderId, details = {}) {
  const r = await fetch(`${PAY_BASE}/paypal/cart-capture-order/${orderId}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(details || {}),
  });
  const d = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(d.detail || "Capture failed");
  return d;
}

// Admin: paid basket orders.
export const getPaidOrders = () => adminGet("/orders");

const genRef = () => Math.random().toString(36).slice(2, 10).toUpperCase();

function openMailto(subject, lines) {
  const body = lines.map(([k, v]) => `${k}: ${v}`).join("\n");
  window.location.href = `mailto:${OWNER_EMAIL}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
}

async function staticSubmit(subject, payload, lines) {
  if (FORM_ENDPOINT) {
    try {
      const res = await fetch(FORM_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ subject, ...payload }),
      });
      if (res.ok) return { delivery: "endpoint" };
    } catch (e) {
      // Endpoint unreachable — fall through to mailto so the user isn't blocked.
      if (process.env.NODE_ENV !== "production") {
        console.warn("Form endpoint failed, falling back to mailto:", e);
      }
    }
  }
  openMailto(subject, lines);
  return { delivery: "mailto" };
}

// ---- Public API (same signatures in both modes) ----
export async function createCustomOrder(data) {
  if (!STATIC_MODE) return http.post("/custom-orders", data).then((r) => r.data);
  const lines = [
    ["Name", data.name], ["Email", data.email], ["Phone", data.phone || "-"],
    ["Event Date", data.event_date], ["Servings", data.servings],
    ["Base Flavor", data.base_flavor], ["Filling", data.filling],
    ["Fulfillment", data.fulfillment], ["Dietary Notes", data.dietary_notes || "-"],
  ];
  const res = await staticSubmit("New Custom Cake Request — Healthy Home Bakery", { type: "custom_order", ...data }, lines);
  return { success: true, order_id: genRef(), ...res };
}

export async function createInquiry(data) {
  if (!STATIC_MODE) return http.post("/inquiries", data).then((r) => r.data);
  const lines = [["Name", data.name], ["Email", data.email], ["Phone", data.phone || "-"], ["Message", data.message]];
  const res = await staticSubmit("New Inquiry — Healthy Home Bakery", { type: "inquiry", ...data }, lines);
  return { success: true, inquiry_id: genRef(), ...res };
}

export async function createDeposit(data) {
  if (!STATIC_MODE) return http.post("/checkout/deposit", data).then((r) => r.data);
  const method = (data.method || "").toLowerCase();
  const reference = genRef();
  const amount = data.amount ?? 25;
  let instructions;
  if (method === "stripe") {
    instructions = STRIPE_LINK
      ? { type: "link", checkout_url: STRIPE_LINK, note: `Complete your $${amount} deposit securely via Stripe. Include reference ${reference} at checkout.` }
      : { type: "manual", note: `Card checkout activates once a Stripe Payment Link is added. For now, please use PayPal or Zelle with reference ${reference}.` };
  } else if (method === "paypal") {
    instructions = { type: "link", paypal_url: PAYPAL_ME, note: `Send your $${amount} deposit via PayPal and include reference ${reference} in the note.` };
  } else if (method === "zelle") {
    instructions = { type: "manual", zelle_email: ZELLE_EMAIL, note: `Send your $${amount} Zelle deposit to the email above and include reference ${reference}.` };
  } else {
    instructions = { type: "manual", note: "Unsupported payment method." };
  }
  return { success: true, reference, amount, method, instructions };
}
